import { useState, ChangeEvent, useEffect, useRef } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Stack,
  Switch,
  FormControlLabel,
  Box,
  Divider,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { Icon } from '@iconify/react';
import useSettings from '../hooks/useSettings';
import useLocalStorage from '../hooks/useLocalStorage';
import { useMediaDevices } from '../hooks/useMediaDevices';
import { MediaDeviceInfo } from '../components/MediaDevicesCheck/MediaDevicesCheck';
import VideoPreview from 'components/Interview/VideoPreview';

const SettingsPage = () => {
  const { onChangeMode } = useSettings();
  // const [emailNotifications, setEmailNotifications] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [settings, setSettings] = useLocalStorage('settings', {
    themeMode: 'light',
    themeDirection: 'ltr',
    themeColor: 'default',
    themeStretch: false
  });
  const { devicesResult } = useMediaDevices();
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('');
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const videoStreamRef = useRef<MediaStream | null>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(false);

  // запрашиваем стрим только при смене разрешений или выбранной камеры
  useEffect(() => {
    let isCancelled = false;

    if (devicesResult?.camera.hasPermission) {
      const startVideoStream = async () => {
        // Останавливаем предыдущий поток
        if (videoStreamRef.current) {
          console.log('[SettingsPage] Останавливаем предыдущий видео-стрим');
          videoStreamRef.current.getTracks().forEach((track) => track.stop());
          videoStreamRef.current = null;
        }

        setIsVideoLoading(true);
        try {
          // Пробуем получить максимально возможное качество для предварительного просмотра
          // Если камера ещё не выбрана (первый заход), используем общие ограничения без deviceId
          const videoConstraints = selectedCamera
            ? [
              // Попытка 1: Максимальное качество с конкретным устройством
              {
                video: {
                  deviceId: { exact: selectedCamera },
                  width: { ideal: 1920, min: 1280 },
                  height: { ideal: 1080, min: 720 },
                  frameRate: { ideal: 30, min: 25 }
                },
                audio: false
              },
              // Попытка 2: HD качество с конкретным устройством
              {
                video: {
                  deviceId: { exact: selectedCamera },
                  width: { ideal: 1280, min: 1024 },
                  height: { ideal: 720, min: 576 },
                  frameRate: { ideal: 30, min: 20 }
                },
                audio: false
              },
              // Попытка 3: Базовое качество с конкретным устройством
              {
                video: {
                  deviceId: { exact: selectedCamera },
                  width: { ideal: 1280 },
                  height: { ideal: 720 },
                  frameRate: { ideal: 30 }
                },
                audio: false
              },
              // Попытка 4: Любое качество с конкретным устройством
              {
                video: {
                  deviceId: { exact: selectedCamera }
                },
                audio: false
              }
            ]
            : [
              // Попытка 1: Максимальное качество без привязки к устройству
              {
                video: {
                  width: { ideal: 1920, min: 1280 },
                  height: { ideal: 1080, min: 720 },
                  frameRate: { ideal: 30, min: 25 }
                },
                audio: false
              },
              // Попытка 2: HD качество
              {
                video: {
                  width: { ideal: 1280, min: 1024 },
                  height: { ideal: 720, min: 576 },
                  frameRate: { ideal: 30, min: 20 }
                },
                audio: false
              },
              // Попытка 3: Базовое качество
              {
                video: {
                  width: { ideal: 1280 },
                  height: { ideal: 720 },
                  frameRate: { ideal: 30 }
                },
                audio: false
              },
              // Попытка 4: Любое доступное качество
              {
                video: true,
                audio: false
              }
            ];

          let stream: MediaStream | null = null;
          let bestSettings: MediaTrackSettings | null = null;

          // Пробуем каждую конфигурацию
          for (const constraints of videoConstraints) {
            if (isCancelled) {
              console.log('[SettingsPage] Отмена: компонент размонтирован во время перебора конфигураций');
              return;
            }
            try {
              // Останавливаем предыдущий стрим перед получением нового
              if (stream) {
                stream.getTracks().forEach((track) => track.stop());
              }
              stream = await navigator.mediaDevices.getUserMedia(constraints);
              const videoTrack = stream.getVideoTracks()[0];
              if (videoTrack) {
                const settings = videoTrack.getSettings();
                // Если это первая успешная попытка или качество лучше предыдущего
                if (
                  !bestSettings ||
                  (settings.width &&
                    settings.height &&
                    settings.width * settings.height >
                    (bestSettings.width || 0) * (bestSettings.height || 0))
                ) {
                  bestSettings = settings;
                  // Если получили хорошее качество, останавливаем поиск
                  if (
                    settings.width &&
                    settings.height &&
                    settings.width * settings.height >= 1280 * 720
                  ) {
                    break;
                  }
                }
              }
            } catch (err) {
              // Продолжаем с следующей конфигурацией
              continue;
            }
          }

          // Если компонент размонтирован — останавливаем полученный стрим и выходим
          if (isCancelled) {
            console.log('[SettingsPage] Отмена: компонент размонтирован после получения стрима, останавливаем');
            stream?.getTracks().forEach((track) => track.stop());
            return;
          }

          if (stream) {
            console.log('[SettingsPage] Камера доступна, стрим получен:', stream.id);
            console.log('[SettingsPage] Настройки видео:', bestSettings);
            videoStreamRef.current = stream;
            setVideoStream(stream);

            // Если камера не была выбрана (первый запуск), пробуем сохранить deviceId из настроек трека
            if (!selectedCamera) {
              try {
                const track = stream.getVideoTracks()[0];
                const settings = track?.getSettings() as MediaTrackSettings & { deviceId?: string };
                if (settings?.deviceId) {
                  setSelectedCamera(settings.deviceId);
                  localStorage.setItem('selectedCamera', settings.deviceId);
                }
              } catch (_) {
                // ignore
              }
            }
          } else {
            throw new Error('Не удалось получить доступ к камере');
          }
        } catch (err) {
          console.error('[SettingsPage] Ошибка при запуске видео-потока:', err);
        } finally {
          if (!isCancelled) {
            setIsVideoLoading(false);
          }
        }
      };

      startVideoStream();
    }

    return () => {
      isCancelled = true;
      if (videoStreamRef.current) {
        console.log('[SettingsPage] Cleanup: останавливаем видео-стрим, треки:', videoStreamRef.current.getTracks().length);
        videoStreamRef.current.getTracks().forEach((track) => {
          console.log('[SettingsPage] Останавливаем трек:', track.kind, track.label, 'readyState:', track.readyState);
          track.stop();
        });
        videoStreamRef.current = null;
      } else {
        console.log('[SettingsPage] Cleanup: videoStreamRef уже null (async ещё не завершился или стрим не был создан)');
      }
    };
  }, [devicesResult?.camera.hasPermission, selectedCamera]);

  // Сначала обнуляем srcObject — на iOS элемент может «залипнуть» на старом стриме.
  // setTimeout для play() — иначе серый экран.
  useEffect(() => {
    const currentVideoRef = videoRef.current;
    if (currentVideoRef && videoStream) {
      currentVideoRef.srcObject = null;
      currentVideoRef.srcObject = videoStream;
      const playTimeout = setTimeout(() => {
        currentVideoRef.play().catch((err) => {
          console.warn('Не удалось воспроизвести видео:', err);
        });
      }, 100);
      return () => {
        clearTimeout(playTimeout);
        currentVideoRef.srcObject = null;
      };
    }

    return () => {
      if (currentVideoRef) {
        currentVideoRef.srcObject = null;
      }
    };
  }, [videoStream]);

  // // Загружаем настройки уведомлений при инициализации
  // useEffect(() => {
  //   const savedNotifications = localStorage.getItem('emailNotifications');
  //   if (savedNotifications !== null) {
  //     setEmailNotifications(JSON.parse(savedNotifications));
  //   }
  // }, []);

  // Сохраняем настройки уведомлений при изменении
  // const handleEmailNotificationsChange = (event: ChangeEvent<HTMLInputElement>) => {
  //   const newValue = event.target.checked;
  //   setEmailNotifications(newValue);
  //   localStorage.setItem('emailNotifications', JSON.stringify(newValue));
  //   setShowSuccessMessage(true);
  // };

  // Обработчик изменения темы
  const handleThemeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newThemeMode = event.target.checked ? 'dark' : 'light';

    // Обновляем настройки напрямую
    const newSettings = {
      ...settings,
      themeMode: newThemeMode
    };
    setSettings(newSettings);

    // Также вызываем оригинальный обработчик для совместимости
    const syntheticEvent = {
      target: { value: newThemeMode }
    } as ChangeEvent<HTMLInputElement>;
    onChangeMode(syntheticEvent);

    setShowSuccessMessage(true);
  };

  const handleCloseSuccessMessage = () => {
    setShowSuccessMessage(false);
  };

  const handleMicrophoneChange = (deviceId: string) => {
    setSelectedMicrophone(deviceId);
    localStorage.setItem('selectedMicrophone', deviceId);
    setShowSuccessMessage(true);
  };

  const handleCameraChange = (deviceId: string) => {
    setSelectedCamera(deviceId);
    localStorage.setItem('selectedCamera', deviceId);
    setShowSuccessMessage(true);
  };

  const getDeviceName = (device: MediaDeviceInfo | null | undefined, defaultName: string) => {
    if (!device) {
      return defaultName;
    }

    if (!device.label) {
      return defaultName;
    }

    return device.label;
  };

  useEffect(() => {
    if (devicesResult && !selectedMicrophone && devicesResult.microphone.devices.length > 0) {
      const deviceId = devicesResult.microphone.devices[0].deviceId;
      setSelectedMicrophone(deviceId);
      localStorage.setItem('selectedMicrophone', deviceId);
    }
    if (devicesResult && !selectedCamera && devicesResult.camera.devices.length > 0) {
      const deviceId = devicesResult.camera.devices[0].deviceId;
      setSelectedCamera(deviceId);
      localStorage.setItem('selectedCamera', deviceId);
    }
  }, [devicesResult, selectedMicrophone, selectedCamera]);

  return (
    <>
      <Box sx={{ overflow: 'auto', height: '100%', mx: -3, px: 3, pb: 8, pt: 3, mt: -3 }}>
        <Card sx={{ minHeight: '151px', mb: 6 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    minWidth: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Icon icon="mdi:microphone" width={24} height={24} color="white" />
                </Box>
                <Box>
                  <Typography variant="h6" component="h2">
                    Микрофон
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Выберите микрофон для записи аудио во время интервью
                  </Typography>
                </Box>
              </Box>


              <Divider />

              {devicesResult?.microphone.devices &&
                devicesResult.microphone.devices.length > 1 ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Выберите микрофон</InputLabel>
                    <Select
                      value={selectedMicrophone}
                      onChange={(e) => handleMicrophoneChange(e.target.value)}
                      label="Выберите микрофон"
                      size='small'
                    >
                      {devicesResult.microphone.devices.map((device) => (
                        <MenuItem key={device.deviceId} value={device.deviceId}>
                          {getDeviceName(device, 'Микрофон')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {getDeviceName(devicesResult?.microphone.devices[0], 'Микрофон')}
                  </Typography>
                )}
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ minHeight: '300px' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    minWidth: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Icon icon="mdi:camera" width={24} height={24} color="white" />
                </Box>
                <Box>
                  <Typography variant="h6" component="h2">
                    Камера
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Выберите камеру для записи видео во время интервью
                  </Typography>
                </Box>
              </Box>


              <Divider />

              {devicesResult?.camera.devices && devicesResult.camera.devices.length > 1 && (
                <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                  <InputLabel>Выберите камеру</InputLabel>
                  <Select
                    value={selectedCamera}
                    onChange={(e) => handleCameraChange(e.target.value)}
                    label="Выберите камеру"
                  >
                    {devicesResult.camera.devices.map((device) => (
                      <MenuItem key={device.deviceId} value={device.deviceId}>
                        {getDeviceName(device, 'Камера')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {devicesResult?.camera.devices && devicesResult.camera.devices.length > 0 && (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {getDeviceName(devicesResult.camera.devices[0], 'Камера')}
                </Typography>
              )}
              {isVideoLoading ? (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : null}
              <Stack spacing={2} alignItems="center">
                {/* Видео-предварительный просмотр */}
                <VideoPreview ref={videoRef} isPreview isRecording hasVideoAccess={devicesResult?.camera.hasPermission} />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
                {/* Настройки темы */}
        {/* <Card sx={{ minHeight: '154px', mt: 6 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    minWidth: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Icon icon="mdi:palette" width={24} height={24} color="white" />
                </Box>
                <Box>
                  <Typography variant="h6" component="h2">
                    Внешний вид
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Выберите тему оформления приложения
                  </Typography>
                </Box>
              </Box>


              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.themeMode === 'dark'}
                    onChange={handleThemeChange}
                    name="themeMode"
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Темная тема
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {settings.themeMode === 'dark' ? 'Включена' : 'Выключена'}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', gap: 2 }}
              />
            </Stack>
          </CardContent>
        </Card> */}

        {/* Настройки уведомлений */}
        {/* <Card>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: 'success.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Icon icon="mdi:email" width={24} height={24} color="white" />
                </Box>
                <Box>
                  <Typography variant="h6" component="h2">
                    Уведомления
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Настройте получение уведомлений на email
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <FormControlLabel
                control={
                  <Switch
                    checked={emailNotifications}
                    onChange={handleEmailNotificationsChange}
                    name="emailNotifications"
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Email уведомления
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {emailNotifications
                        ? 'Включены - вы будете получать уведомления на email'
                        : 'Выключены - уведомления на email не будут приходить'}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', gap: 2 }}
              />

              {emailNotifications && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Уведомления будут приходить на email, указанный в вашем профиле, при:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                    <li>Завершении анализа интервью</li>
                    <li>Готовности результатов оценки</li>
                    <li>Важных обновлениях системы</li>
                  </Box>
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card> */}

        {/* Дополнительная информация */}
        {/* <Card sx={{ backgroundColor: (theme) => theme.palette.background.paper }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Icon icon="mdi:information" width={24} height={24} color="primary" />
              <Typography variant="h6" component="h3">
                Информация
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Все настройки сохраняются автоматически и применяются немедленно. Вы можете изменить
              их в любое время.
            </Typography>
          </CardContent>
        </Card> */}
      </Box>

      {/* Уведомление об успешном сохранении */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={handleCloseSuccessMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccessMessage} severity="success" sx={{ width: '100%' }}>
          Настройки сохранены
        </Alert>
      </Snackbar>
    </>
  );
};

export default SettingsPage;
